import { createWithEqualityFn } from 'zustand/traditional';
import {
  Roles,
  OrganizationDashboardUser,
  OrganizationDashboardUserSort,
  OrganizationDashboardUserUpdateRequest,
} from '@youscience/commonapp-service-common';

import { ALL_TYPES_VALUE } from '@constants';

import { Order } from '@interfaces';

import { commonAppService } from '@services/commonApp.service';
import useNotificationStore from '@store/NotificationStore';

export type FilterByRole = typeof ALL_TYPES_VALUE | Roles;
export type UserToUpdate = OrganizationDashboardUserUpdateRequest['users'][0];

export interface TableSearchParams {
  filterByRole: FilterByRole;
  searchValue: string;
  sortDirection: Order;
}
type UserId = OrganizationDashboardUser['userId'];

export interface OrganizationDashboardStore {
  tableData: Map<UserId, OrganizationDashboardUser>;
  tableSearchParams: TableSearchParams;
  isLoadingTableData: boolean;
  setTableSearchParams: (params: Partial<TableSearchParams>) => void;
  getTableData: () => void;
  setTableRow: (row: OrganizationDashboardUser) => void;
  updateUsers: (usersToEdit: UserToUpdate[]) => void;
}

const INITIAL_STATE = {
  isLoadingTableData: false,
  tableData: new Map(),
  tableSearchParams: {
    filterByRole: ALL_TYPES_VALUE,
    sortDirection: Order.asc,
  } as Partial<TableSearchParams>,
} as OrganizationDashboardStore;

const userArrayToMap = (userArray: OrganizationDashboardUser[]) =>
  new Map(userArray.map((userData) => [userData.userId, userData]));

const ENDPOINTS = {
  organizationDashboard: 'recommender/organization-dashboard',
} as const;

export const useOrganizationDashboardStore = createWithEqualityFn<OrganizationDashboardStore>()(
  (set, get) => ({
    ...INITIAL_STATE,

    setTableSearchParams: (params: Partial<TableSearchParams>) => {
      set({
        tableSearchParams: { ...get().tableSearchParams, ...params },
      });
    },

    setTableRow: (row: OrganizationDashboardUser) => {
      const { tableData } = get();

      const tableDataClone = new Map(tableData);
      tableDataClone.set(row.userId, row);

      set({ tableData: tableDataClone });
    },

    getTableData: async () => {
      set({
        isLoadingTableData: true,
      });

      const {
        tableSearchParams: { filterByRole, searchValue, sortDirection },
      } = get();

      const params: Record<string, unknown> = {
        legalName: searchValue,
        role: filterByRole === ALL_TYPES_VALUE ? undefined : filterByRole,
        sort:
          sortDirection === Order.asc
            ? OrganizationDashboardUserSort.LEGALNAME_ASC
            : OrganizationDashboardUserSort.LEGALNAME_DESC,
      };

      const organizationDashboardUsers = await commonAppService.getAsync<OrganizationDashboardUser[]>(
        ENDPOINTS.organizationDashboard,
        { params },
      );

      set({
        isLoadingTableData: false,
        tableData: userArrayToMap(organizationDashboardUsers),
      });
    },

    updateUsers: async (usersToEdit: UserToUpdate[]) => {
      set({
        isLoadingTableData: true,
      });

      try {
        const payload = { users: usersToEdit };

        await commonAppService.patchAsync<OrganizationDashboardUser[], OrganizationDashboardUserUpdateRequest>(
          ENDPOINTS.organizationDashboard,
          payload,
        );

        useNotificationStore.getState().notify({
          message: `Success! Changes have been saved.`,
          severity: 'success',
        });

        set({
          isLoadingTableData: false,
        });
      } catch (error) {
        set({
          isLoadingTableData: false,
        });
      }
    },
  }),
  Object.is,
);
